import { c as _c } from "react/compiler-runtime";
// «Личные данные» по макету «Профиль» (кадр 8): аватар 64 + «Изменить фото
// профиля»; поля 810×48 r20 на #1F1F1F 3 % с подписью 14px сверху — Имя,
// Фамилия, Отчество; «Пол» — чипы h40 r20 (выбранный: #FA5C2E 5 % + обводка
// 0.5 #FA5C2E); Дата рождения (ДД.ММ.ГГГГ); Телефон — только чтение, карандаш
// открывает смену номера (код на текущий → новый номер → код на новый); Почта;
// «Сохранить изменения» 40px r10 #2E2E2E. Под формой — ссылки на Адреса и
// Устройства: их нет в кадре, но разделы кабинета терять нельзя.

import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@osmix/ui/dialog";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { api } from "@/api";
import { Edit20Icon } from "@/components/icons";
import { DeleteAccount } from "@/components/profile/delete-account";
import { OAuthLinks } from "@/components/profile/oauth-links";
import { Avatar, ProfilePanel } from "@/components/profile/profile-shell";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/chip";
import { Input } from "@/components/ui/text-field";
import { useProfile } from "@/store";
const OVERLAY = "bg-black/20 backdrop-blur-[2px]";

// ISO «1990-06-12» ⇄ «12.06.1990».
const isoToRu = (iso?: string) => {
  const m = iso?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : "";
};
const ruToIso = (ru: string) => {
  const m = ru.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
};
const maskDate = (raw: string) => {
  const d = raw.replace(/\D/g, "").slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join(".");
};
// «+7 (900) 000 - 00 - 00» — как в макете.
const prettyPhone = (phone: string) => {
  const d = phone.replace(/\D/g, "").replace(/^[78]/, "").slice(0, 10);
  if (d.length < 10) return phone;
  return `+7 (${d.slice(0, 3)}) ${d.slice(3, 6)} - ${d.slice(6, 8)} - ${d.slice(8, 10)}`;
};
export function ProfilePersonalPage() {
  const profile = useProfile(s => s.profile);
  const user = useProfile(s_0 => s_0.user);
  const hydrateMe = useProfile(s_1 => s_1.hydrateMe);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    patronymic: "",
    gender: undefined as "female" | "male" | undefined,
    birth: "",
    email: ""
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!profile) return;
    // Старые профили знают только общее «name» — раскладываем его в Имя/Фамилию.
    const [first = "", ...rest] = (profile.name ?? "").split(" ");
    setForm({
      firstName: profile.firstName ?? (profile.lastName ? "" : first),
      lastName: profile.lastName ?? (profile.firstName ? "" : rest.join(" ")),
      patronymic: profile.patronymic ?? "",
      gender: profile.gender,
      birth: isoToRu(profile.birthDate),
      email: profile.email ?? ""
    });
  }, [profile]);
  if (!profile || !user) return null;
  const fullName = [form.firstName, form.lastName].filter(Boolean).join(" ") || profile.name;
  const save = async () => {
    const birthDate = form.birth ? ruToIso(form.birth) : "";
    if (birthDate === null) {
      toast.error("Дата рождения — в формате ДД.ММ.ГГГГ");
      return;
    }
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Проверьте адрес почты");
      return;
    }
    setSaving(true);
    try {
      await api.buyer.updateProfileExt({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        patronymic: form.patronymic.trim(),
        ...(form.gender ? {
          gender: form.gender
        } : {}),
        ...(birthDate ? {
          birthDate
        } : {}),
        email: form.email.trim()
      });
      await hydrateMe();
      toast.success("Изменения сохранены");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };
  const upload = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Нужна картинка: JPG, PNG или WebP");
      return;
    }
    setUploading(true);
    try {
      await api.buyer.uploadAvatar(file);
      await hydrateMe();
      toast.success("Фото профиля обновлено");
    } catch (e_0) {
      toast.error(e_0 instanceof Error ? e_0.message : "Не удалось загрузить фото");
    } finally {
      setUploading(false);
    }
  };
  const set = <K extends keyof typeof form,>(key: K, value: (typeof form)[K]) => setForm(f => ({
    ...f,
    [key]: value
  }));
  return <ProfilePanel title="Личные данные">
			<div className="flex items-center gap-3">
				<button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} aria-label="Изменить фото профиля" className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">
					<Avatar url={profile.avatarUrl} name={fullName} size={65} />
				</button>
				<button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-sm text-[14px] leading-[18px] text-[#1F1F1F] outline-none hover:underline focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">
					{uploading ? "Загружаем…" : "Изменить фото профиля"}
				</button>
				<input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={e_1 => {
        const file_0 = e_1.target.files?.[0];
        e_1.target.value = "";
        if (file_0) void upload(file_0);
      }} />
			</div>

			<form className="mt-4 flex flex-col gap-4" onSubmit={e_2 => {
      e_2.preventDefault();
      void save();
    }}>
				<Field label="Имя">
					<Input size="lg" value={form.firstName} onChange={e_3 => set("firstName", e_3.target.value)} autoComplete="given-name" />
				</Field>
				<Field label="Фамилия">
					<Input size="lg" value={form.lastName} onChange={e_4 => set("lastName", e_4.target.value)} autoComplete="family-name" />
				</Field>
				<Field label="Отчество">
					<Input size="lg" value={form.patronymic} onChange={e_5 => set("patronymic", e_5.target.value)} autoComplete="additional-name" />
				</Field>
				<div>
					<p className="mb-2 text-[14px] leading-[18px] font-medium text-[#171A24]/64">Пол</p>
					<div className="flex gap-2.5" role="radiogroup" aria-label="Пол">
						{([["female", "женский"], ["male", "мужской"]] as const).map(([value_0, label]) =>
          // Чипы пола в кадре h40, 16/21 — крупнее стандартного DS-чипа.
          <Chip key={value_0} role="radio" aria-checked={form.gender === value_0} selected={form.gender === value_0} onClick={() => set("gender", value_0)} className="h-10 px-4 text-[16px] leading-[21px]">
								{label}
							</Chip>)}
					</div>
				</div>
				<Field label="Дата рождения">
					<Input size="lg" value={form.birth} onChange={e_6 => set("birth", maskDate(e_6.target.value))} placeholder="ДД.ММ.ГГГГ" inputMode="numeric" autoComplete="bday" />
				</Field>
				<Field label="Телефон">
					<Input size="lg" value={prettyPhone(user.phone)} readOnly aria-readonly="true" end={<button type="button" onClick={() => setPhoneOpen(true)} aria-label="Изменить номер телефона" className="-mr-1.5 grid size-8 place-items-center rounded-full text-ds-primary outline-none hover:bg-white focus-visible:ring-2 focus-visible:ring-ds-focus">
								<Edit20Icon size={20} />
							</button>} />
				</Field>
				<Field label="Почта">
					<Input size="lg" type="email" value={form.email} onChange={e_7 => set("email", e_7.target.value)} placeholder="example@example.ru" autoComplete="email" />
				</Field>
				<Button type="submit" size="md" disabled={saving} className="self-start bg-ds-ink-2 hover:bg-ds-primary">
					{saving ? "Сохраняем…" : "Сохранить изменения"}
				</Button>
			</form>

			<div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[14px] text-[#1F1F1F]/64">
				<Link to="/profile/addresses" className="rounded-sm outline-none hover:text-[#1F1F1F] hover:underline focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">
					Адреса доставки
				</Link>
				<Link to="/profile/devices" className="rounded-sm outline-none hover:text-[#1F1F1F] hover:underline focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">
					Устройства и сеансы
				</Link>
			</div>

			<OAuthLinks />
			<DeleteAccount />

			<PhoneChangeDialog open={phoneOpen} onOpenChange={setPhoneOpen} onChanged={() => void hydrateMe()} />
		</ProfilePanel>;
}
function Field(t0) {
  const $ = _c(5);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-[14px] leading-[18px] font-medium text-[#171A24]/64">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <label className="flex flex-col gap-2">{t1}{children}</label>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

// Смена номера — 4 шага шлюза: код на текущий номер → новый номер → код на новый.
function PhoneChangeDialog({
  open,
  onOpenChange,
  onChanged
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void;
}) {
  const [step, setStep] = useState<"current" | "phone" | "new">("current");
  const [sessionId, setSessionId] = useState("");
  const [masked, setMasked] = useState("");
  const [code, setCode] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setStep("current");
    setCode("");
    setPhone("");
    setBusy(true);
    api.buyer.startPhoneChange().then(s => {
      setSessionId(s.sessionId);
      setMasked(s.maskedTarget);
    }).catch(e => {
      toast.error(e instanceof Error ? e.message : "Не удалось отправить код");
      onOpenChange(false);
    }).finally(() => setBusy(false));
  }, [open, onOpenChange]);
  const next = async () => {
    setBusy(true);
    try {
      if (step === "current") {
        const r = await api.buyer.verifyPhoneChangeCurrent(sessionId, code);
        if (!r.success) {
          toast.error(`Неверный код. Осталось попыток: ${r.attemptsLeft}`);
          return;
        }
        setCode("");
        setStep("phone");
      } else if (step === "phone") {
        const digits = phone.replace(/\D/g, "").replace(/^[78]/, "");
        if (digits.length !== 10) {
          toast.error("Введите номер полностью");
          return;
        }
        const r_0 = await api.buyer.setPhoneChangeNew(sessionId, `+7${digits}`);
        setMasked(r_0.maskedTarget);
        setStep("new");
      } else {
        await api.buyer.verifyPhoneChangeNew(sessionId, code);
        toast.success("Номер телефона изменён");
        onChanged();
        onOpenChange(false);
      }
    } catch (e_0) {
      toast.error(e_0 instanceof Error ? e_0.message : "Не удалось изменить номер");
    } finally {
      setBusy(false);
    }
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent overlayClassName={OVERLAY} className="gap-3 rounded-[20px] border-0 bg-white p-4 pt-[18px] sm:max-w-[366px]">
				<DialogTitle className="ds-h3 text-ds-ink">Смена номера</DialogTitle>
				<DialogDescription className="text-[14px] leading-[18px] text-[#1F1F1F]">
					{step === "phone" ? "Введите новый номер телефона" : `Мы отправили код на номер ${masked}`}
				</DialogDescription>
				{step === "phone" ? <Input size="lg" value={phone} onChange={e_1 => setPhone(e_1.target.value)} placeholder="+7 900 000 00 00" inputMode="tel" autoFocus /> : <Input size="lg" value={code} onChange={e_2 => setCode(e_2.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Код из СМС" inputMode="numeric" autoComplete="one-time-code" autoFocus />}
				<Button block disabled={busy || step !== "phone" && code.length < 4} onClick={() => void next()}>
					{step === "new" ? "Подтвердить" : "Продолжить"}
				</Button>
			</DialogContent>
		</Dialog>;
}